import React from "react";
import { PulseLoader } from "react-spinners";

export type ButtonSize = "sm" | "md" | "lg";
export type ButtonVariant = "solid" | "ghost";
/** stretch: full-width form CTA (auth). inline: modal footers. toolbar: primary row on mobile. */
export type ButtonLayout = "stretch" | "inline" | "toolbar";

interface ButtonProps {
  label: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  isLoading?: boolean;
  disabled?: boolean;
  /** Default matches legacy form buttons: full width with max 450px on larger screens. */
  layout?: ButtonLayout;
  size?: ButtonSize;
  variant?: ButtonVariant;
  /** Appended to layout width rules (e.g. min-w-[250px] for wide inline actions). */
  minWidthClass?: string;
  classNames?: string;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-10 px-4 text-sm",
  md: "h-[54px] px-4 text-base",
  lg: "h-14 px-6 text-base",
};

const variantClasses: Record<ButtonVariant, string> = {
  solid: "bg-primary text-white hover:bg-primaryHover",
  ghost:
    "bg-transparent text-gray-900 hover:bg-gray-100 border border-transparent",
};

const layoutClasses: Record<ButtonLayout, string> = {
  stretch: "w-full max-w-[450px] mx-auto",
  inline: "w-auto shrink-0",
  toolbar: "w-full sm:w-auto shrink-0",
};

const Button: React.FC<ButtonProps> = ({
  label,
  onClick,
  type = "button",
  isLoading,
  disabled,
  layout = "stretch",
  size = "md",
  variant = "solid",
  minWidthClass,
  classNames = "",
}) => {
  const resolvedMinWidth =
    minWidthClass ??
    (layout === "inline" ? "min-w-[150px]" : "");

  const loaderSize = size === "sm" ? 6 : 10;
  const loaderColor = variant === "ghost" ? "#3F2EC8" : "#fff";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`flex items-center justify-center rounded-[50px] font-medium ease-in-out transition-all ${sizeClasses[size]} ${variantClasses[variant]} ${layoutClasses[layout]} ${resolvedMinWidth} ${
        isLoading || disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${classNames}`.trim()}
    >
      {isLoading ? (
        <PulseLoader color={loaderColor} size={loaderSize} />
      ) : (
        label
      )}
    </button>
  );
};

export default Button;
